import api from '../../lib/api';
import type {
  AssessRiskPayload,
  ComparativeResult,
  ContextPanelData,
  CreateAssessmentPayload,
  DashboardFilters,
  DashboardKpis,
  EvaluationMethod,
  HeatmapData,
  MatrixCell,
  PaginatedRisks,
  RiskAction,
  RiskActionPayload,
  RiskAssessment,
  RiskDashboardData,
  RiskEntry,
  RiskImportPreview,
  RiskImportResult,
  RiskPayload,
  StructureNodeSummary,
} from './types';

export interface ListRisksParams {
  page?: number;
  pageSize?: number;
  search?: string;
  kind?: string;
  status?: string;
  scope?: string;
  studyId?: string;
  activityId?: string;
  processArea?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface MatrixParams {
  kind?: string;
  status?: string;
}

export const fetchRisks = async (params: ListRisksParams = {}): Promise<PaginatedRisks> => {
  const { data } = await api.get<PaginatedRisks>('risks', { params });
  return data;
};

export const fetchRiskMatrix = async (params: MatrixParams = {}): Promise<{ cells: MatrixCell[] }> => {
  const { data } = await api.get<{ cells: MatrixCell[] }>('risks/matrix', { params });
  return data;
};

export const fetchRiskHeatmap = async (params: DashboardFilters = {}): Promise<HeatmapData> => {
  const { data } = await api.get<HeatmapData>('risks/heatmap', { params });
  return data;
};

export const fetchRiskDashboard = async (params: DashboardFilters = {}): Promise<RiskDashboardData> => {
  const { data } = await api.get<RiskDashboardData>('risks/dashboard', { params });
  return data;
};

export const fetchRiskDashboardKpis = async (params: DashboardFilters = {}): Promise<DashboardKpis> => {
  const { data } = await api.get<DashboardKpis>('risks/dashboard/kpis', { params });
  return data;
};

export const fetchRiskStructureBoard = async (
  params: DashboardFilters = {},
): Promise<StructureNodeSummary[]> => {
  const { data } = await api.get<StructureNodeSummary[]>('risks/dashboard/structure', { params });
  return data;
};

export const fetchRiskContextPanel = async (
  params: DashboardFilters = {},
): Promise<ContextPanelData> => {
  const { data } = await api.get<ContextPanelData>('risks/dashboard/context', { params });
  return data;
};

export const fetchRiskComparative = async (
  params: DashboardFilters = {},
): Promise<ComparativeResult> => {
  const { data } = await api.get<ComparativeResult>('risks/dashboard/comparative', { params });
  return data;
};

export const fetchRisk = async (id: string): Promise<RiskEntry> => {
  const { data } = await api.get<RiskEntry>(`risks/${id}`);
  return data;
};

export const createRisk = async (payload: RiskPayload): Promise<RiskEntry> => {
  const { data } = await api.post<RiskEntry>('risks', payload);
  return data;
};

export const updateRisk = async (id: string, payload: Partial<RiskPayload>): Promise<RiskEntry> => {
  const { data } = await api.put<RiskEntry>(`risks/${id}`, payload);
  return data;
};

export const assessRisk = async (id: string, payload: AssessRiskPayload): Promise<RiskEntry> => {
  const { data } = await api.post<RiskEntry>(`risks/${id}/assess`, payload);
  return data;
};

export const createRiskAssessment = async (
  riskId: string,
  payload: CreateAssessmentPayload,
): Promise<RiskAssessment> => {
  const { data } = await api.post<RiskAssessment>(`risks/${riskId}/assessments`, payload);
  return data;
};

export const fetchRiskAssessments = async (riskId: string): Promise<RiskAssessment[]> => {
  const { data } = await api.get<RiskAssessment[]>(`risks/${riskId}/assessments`);
  return data;
};

export const syncRiskOrigin = async (id: string): Promise<RiskEntry> => {
  const { data } = await api.post<RiskEntry>(`risks/${id}/sync-origin`);
  return data;
};

export const fetchEvaluationMethods = async (): Promise<EvaluationMethod[]> => {
  const { data } = await api.get<EvaluationMethod[]>('evaluation-methods');
  return data;
};

export const deleteRisk = async (id: string): Promise<void> => {
  await api.delete(`risks/${id}`);
};

export const createRiskAction = async (
  riskId: string,
  payload: RiskActionPayload,
): Promise<RiskAction> => {
  const { data } = await api.post<RiskAction>(`risks/${riskId}/actions`, payload);
  return data;
};

export const updateRiskAction = async (
  actionId: string,
  payload: Partial<RiskActionPayload>,
): Promise<RiskAction> => {
  const { data } = await api.patch<RiskAction>(`risks/actions/${actionId}`, payload);
  return data;
};

export const escalateRiskActionToNc = async (
  actionId: string,
): Promise<{ action: RiskAction; qmsRecordId: string }> => {
  const { data } = await api.post<{ action: RiskAction; qmsRecordId: string }>(
    `risks/actions/${actionId}/escalate-nc`,
  );
  return data;
};

export const linkRiskObjective = async (
  riskId: string,
  objectiveId: string,
): Promise<{ id: string; code: string; title: string; status: string }> => {
  const { data } = await api.post<{ id: string; code: string; title: string; status: string }>(
    `risks/${riskId}/objectives`,
    { objectiveId },
  );
  return data;
};

export const unlinkRiskObjective = async (riskId: string, objectiveId: string): Promise<void> => {
  await api.delete(`risks/${riskId}/objectives/${objectiveId}`);
};

export const importRisksPreview = async (file: File): Promise<RiskImportPreview> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<RiskImportPreview>('risks/import/preview', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const importRisksWorkbook = async (file: File): Promise<RiskImportResult> => {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<RiskImportResult>('risks/import', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const downloadRisksExport = async (params?: {
  scope?: string;
  studyId?: string;
}): Promise<void> => {
  const { data } = await api.get<Blob>('risks/export', {
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'riesgos-export.xlsx';
  anchor.click();
  URL.revokeObjectURL(url);
};

export const downloadRisksTemplate = async (format: 'fq22' | 'fgl05' = 'fq22'): Promise<void> => {
  const { data } = await api.get<Blob>('risks/template', {
    params: { format },
    responseType: 'blob',
  });
  const filename = format === 'fgl05' ? 'F-GL-05-ejemplo.xlsx' : 'FQ-22-ejemplo.xlsx';
  const url = URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const updateEvaluationMethodThresholds = async (
  methodId: string,
  thresholds: { maxValue: number; label: string; color: string }[],
): Promise<EvaluationMethod> => {
  const { data } = await api.put<EvaluationMethod>(`evaluation-methods/${methodId}/thresholds`, {
    thresholds,
  });
  return data;
};

export const fetchOverdueRiskActions = async (): Promise<RiskAction[]> => {
  const { data } = await api.get<RiskAction[]>('risks/actions/overdue');
  return data;
};
